import { useState } from "react";
import { Link } from "react-router";
import { EyeCloseIcon, EyeIcon } from "../../icons";
import Label from "../form/Label";
import Input from "../form/input/InputField";
import Checkbox from "../form/input/Checkbox";
import { request } from "../../utils";
import VerifyAccountModal from "../ui/modal/VerifyAccountModal";
import Alert from "../ui/alert/Alert";
import { useModal } from "../../hooks/useModal";
import TermsAndConditionsModal from "../ui/modal/TermsAndConditionsModal";
import { isBusinessEmail } from "../../utils/helpers";
import PhoneNumberInput from "../form/input/PhoneNumberInput";

export default function SignUpForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [contactPerson, setContactPerson] = useState({
    firstName: "",
    lastName: "",
  });
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [error, setError] = useState("");
  const [phone, setPhone] = useState("");


  // ---- Modals
  const {
    isOpen: isTermsOpen,
    openModal: openTerms,
    closeModal: closeTerms,
  } = useModal();

  // ---- handle input change
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setContactPerson({ ...contactPerson, [name]: value });
  };

  // ---- handle registration
  const handleRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (confirmPassword !== password) {
        setError("Passwords do not match");
        setTimeout(() => setError(""), 3000);
        setLoading(false);
        return;
      }

      if (!isBusinessEmail(email)) {
        setError("Please use your official business email.");
        setTimeout(() => setError(""), 3000);
        setLoading(false);
        return;
      }

      if (
        !email ||
        !businessName ||
        !contactPerson.firstName ||
        !contactPerson.lastName || !phone
      ) {
        setError("Please fill in all required fields.");
        setTimeout(() => setError(""), 3000);
        setLoading(false);
        return;
      }

      const normalizeEmail = email.toLowerCase().trim();

      const formData = {
        email: normalizeEmail,
        businessName,
        contactPerson,
        password,
        phoneNumber: phone
      };

      const response = await request.post({
        url: "/user",
        data: formData,
        auth: false,
      });

      if (response.success) {
        setShowOtpModal(true);
        return;
      } else {
        setError(response.message || "Something went wrong.");
        setTimeout(() => setError(""), 3000);
      }
    } catch (error) {
      setError((error as Error).message);
      setTimeout(() => setError(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  // ---- ---- ---- Password Validators start ---- ---- ----
  const passwordCriteria = {
    length: (password: string) => password.length >= 6 && password.length <= 50,
    hasLetterAndNumber: (password: string) => /[a-zA-Z]/.test(password) && /[0-9]/.test(password),
    hasUpperCase: (password: string) => /[A-Z]/.test(password),
    hasSpecialChar: (password: string) => /[^a-zA-Z0-9]/.test(password),
  };

  const getPasswordStatus = (password: string) => ({
    length: passwordCriteria.length(password),
    hasLetterAndNumber: passwordCriteria.hasLetterAndNumber(password),
    hasUpperCase: passwordCriteria.hasUpperCase(password),
    hasSpecialChar: passwordCriteria.hasSpecialChar(password),
  });

  const passwordStatus = getPasswordStatus(password);

  const strength = Object.values(passwordStatus).filter(Boolean).length;

  const isPasswordValid =
    passwordStatus.length &&
    passwordStatus.hasLetterAndNumber &&
    passwordStatus.hasUpperCase &&
    passwordStatus.hasSpecialChar;

  // ---- ---- ---- Password Validators End ---- ---- ----

  return (
    <div className="flex flex-col flex-1 w-full overflow-y-auto lg:w-1/2 no-scrollbar mt-5 mb-5">
      <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
        <div>
          <div className="mb-5 sm:mb-8">
            <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
              Create your account!
            </h1>
          </div>
          <div>
            <form onSubmit={handleRegistration}>
              <div className="space-y-5">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {/* <!-- First Name --> */}
                  <div className="sm:col-span-1">
                    <Label>
                      First Name<span className="text-error-500">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="firstName"
                      value={contactPerson.firstName}
                      onChange={handleChange}
                      placeholder="Enter your first name"
                      required
                    />
                  </div>

                  {/* <!-- Last Name --> */}
                  <div className="sm:col-span-1">
                    <Label>
                      Last Name<span className="text-error-500">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="lastName"
                      value={contactPerson.lastName}
                      onChange={handleChange}
                      placeholder="Enter your last name"
                      required
                    />
                  </div>
                </div>

                {/* <!-- Business Name --> */}
                <div>
                  <Label>
                    Business Name<span className="text-error-500">*</span>
                  </Label>
                  <Input
                    type="text"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="Enter your Business name"
                    required
                  />
                </div>

                {/* <!-- Phone Number --> */}
                <div>
                  <Label>
                    Phone Number<span className="text-error-500">*</span>
                  </Label>
                  <PhoneNumberInput
                  value={phone}
                  onChange={setPhone}
                  defaultCountry="AF"
                  required
                />
                </div>

                {/* <!-- Email --> */}
                <div>
                  <Label>
                    Business Email<span className="text-error-500">*</span>
                  </Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                  />
                </div>

                {/* <!-- Password --> */}
                <div>
                  <Label>
                    Password<span className="text-error-500">*</span>
                  </Label>

                  {/* password bar */}
                  {password.length > 0 && (
                    <div className="mt-2 mb-2 w-full h-2 bg-gray-200 rounded">
                      <div
                        className={`h-full rounded transition-all duration-300 ${
                          strength === 4
                            ? "bg-green-500 w-full"
                            : strength === 3
                            ? "bg-yellow-500 w-3/4"
                            : strength === 2
                            ? "bg-orange-400 w-1/2"
                            : strength === 1
                            ? "bg-red-400 w-1/4"
                            : "w-0"
                        }`}
                      ></div>
                    </div>
                  )}

                  <div className="relative">
                    <Input
                      placeholder="Enter your password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                    >
                      {showPassword ? (
                        <EyeIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                      ) : (
                        <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                      )}
                    </span>
                  </div>

                  {/* Password Validators  */}
                  {password.length > 0 && (
                    <div className="mt-2 space-y-1 text-sm">
                      <div
                        className={`flex items-center gap-2 ${
                          passwordStatus.length
                            ? "text-green-600"
                            : "text-gray-500"
                        }`}
                      >
                        <span
                          className="w-2 h-2 rounded-full inline-block"
                          style={{
                            backgroundColor: passwordStatus.length
                              ? "#22c55e"
                              : "#9ca3af",
                          }}
                        ></span>
                        At least 6 characters (max 50)
                      </div>

                      <div
                        className={`flex items-center gap-2 ${
                          passwordStatus.hasLetterAndNumber
                            ? "text-green-600"
                            : "text-gray-500"
                        }`}
                      >
                        <span
                          className="w-2 h-2 rounded-full inline-block"
                          style={{
                            backgroundColor: passwordStatus.hasLetterAndNumber
                              ? "#22c55e"
                              : "#9ca3af",
                          }}
                        ></span>
                        Contains both letters and numbers
                      </div>

                      <div
                        className={`flex items-center gap-2 ${
                          passwordStatus.hasUpperCase
                            ? "text-green-600"
                            : "text-gray-500"
                        }`}
                      >
                        <span
                          className="w-2 h-2 rounded-full inline-block"
                          style={{
                            backgroundColor: passwordStatus.hasUpperCase
                              ? "#22c55e"
                              : "#9ca3af",
                          }}
                        ></span>
                        Contains at least one uppercase letter
                      </div>

                      <div
                        className={`flex items-center gap-2 ${
                          passwordStatus.hasSpecialChar
                            ? "text-green-600"
                            : "text-gray-500"
                        }`}
                      >
                        <span
                          className="w-2 h-2 rounded-full inline-block"
                          style={{
                            backgroundColor: passwordStatus.hasSpecialChar
                              ? "#22c55e"
                              : "#9ca3af",
                          }}
                        ></span>
                        Contains at least a special character
                      </div>
                    </div>
                  )}
                </div>

                {/* <!--Confirm Password --> */}
                <div>
                  <Label>
                    Confirm Password<span className="text-error-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      placeholder="Enter your password"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                    <span
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                    >
                      {showConfirmPassword ? (
                        <EyeIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                      ) : (
                        <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                      )}
                    </span>
                  </div>
                </div>

                {/* <!-- Checkbox --> */}
                <div className="flex items-center gap-3">
                  <Checkbox
                    className="w-5 h-5"
                    checked={isChecked}
                    onChange={setIsChecked}
                  />
                  <p className="inline-block font-normal text-gray-500 dark:text-gray-400 text-sm">
                    By creating an account, you agree to our{" "}
                    <span
                      className="text-gray-800 dark:text-white/90 underline cursor-pointer"
                      onClick={openTerms}
                    >
                      Terms and Conditions
                    </span>
                  </p>
                </div>

                {error && (
                  <div className="mb-6">
                    <Alert
                      variant="error"
                      title="Error"
                      message={error}
                      showLink={false}
                    />
                  </div>
                )}

                {/* <!-- Button --> */}
                <div>
                  <button
                    disabled={!isChecked || loading || !isPasswordValid}
                    className={`flex items-center justify-center w-full px-4 py-3 text-sm font-medium ${
                      !isChecked || !isPasswordValid
                        ? "text-white transition rounded-lg bg-brand-300 cursor-not-allowed shadow-theme-xs "
                        : "text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600"
                    }`}
                  >
                    {loading ? "Signing Up..." : "Sign Up"}
                  </button>
                </div>
              </div>
            </form>

            <div className="mt-5">
              <p className="text-sm font-normal text-center text-gray-700 dark:text-gray-400 sm:text-start">
                Already have an account? {""}
                <Link
                  to="/"
                  className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
                >
                  Login
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Terms and Condition modal  */}
      <TermsAndConditionsModal
        isOpen={isTermsOpen}
        onClose={closeTerms}
        onAccept={() => {
          setIsChecked(true);
          closeTerms();
        }}
      />

      {/* New user Verification of OTP */}
      {showOtpModal && (
        <VerifyAccountModal
          isOpen={showOtpModal}
          onClose={() => setShowOtpModal(false)}
          email={email.toLowerCase().trim()}
        />
      )}
    </div>
  );
}
